import { Injectable } from '@angular/core';
import { RecentlyPurchasedService } from '@app/lists/pages/recently-purchased/services/recently-purchased.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PlatformService } from '@panamax/app-state';
import { DownloadListOptions, RECENT_PURCHASE_ACTIONS } from '@usf/ngrx-list';
import { ProductPricingService } from '@usf/ngrx-pricing';
import {
  LoadingState,
  ProductStateService,
  selectProductDetails,
} from '@usf/ngrx-product';
import { combineLatest, iif, mergeMap, of } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { DownloadListService } from '../../../shared/services/document/download-list/download-list.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';

@Injectable({
  providedIn: 'root',
})
export class ClientRecentPurchaseEffects {
  downloadList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECENT_PURCHASE_ACTIONS.downloadList),
        // Load products and prices
        tap(action => {
          this.loadProductsAndPricingForList(action.downloadListOptions);
        }),
        // Make sure product/price data is loaded before attempting to download
        mergeMap(action => {
          const loadingSelector = action.downloadListOptions
            .includeProductPrices
            ? this.recentlyPurchasedService.isRPAndProductPricingDataLoaded$(
                action.downloadListOptions.includeNutritionals,
              )
            : this.recentlyPurchasedService.isRPAndProductDataLoaded$(
                action.downloadListOptions.includeNutritionals,
              );
          return combineLatest([of(action), loadingSelector]).pipe(
            filter(
              ([action, loadingState]) =>
                loadingState === LoadingState.loaded || loadingState === true,
            ),
            take(1),
          );
        }),
        mergeMap(([action, loadingState]) => {
          return combineLatest([
            of(action),
            this.recentlyPurchasedService.selectRpVm$(),
            this.recentlyPurchasedService.recentlyPurchasedProductNumbers$(),
          ]).pipe(take(1));
        }),
        mergeMap(([action, viewmodel, numbers]) => {
          return combineLatest([
            of(action),
            of(viewmodel),
            iif(
              () => action.downloadListOptions.includeNutritionals,
              this.store.select(selectProductDetails(numbers)),
              of([]),
            ),
          ]).pipe(take(1));
        }),
        tap(([action, viewModel, details]) => {
          this.downloadListService.downloadList(
            action.downloadListOptions,
            'N/A',
            viewModel?.items,
            details,
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  deleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECENT_PURCHASE_ACTIONS.deleteSuccess),
        tap(action => {
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.recentlyPurchasedPage.yourProductHasBeenRemoved',
            ),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  deleteFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECENT_PURCHASE_ACTIONS.deleteFail),
        tap(action => {
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.recentlyPurchasedPage.thereWasAnIssue',
            ),
            'red-toast',
            MessageTypeEnum.failure,
            [],
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  loadProductsAndPricingForList(downloadListOptions: DownloadListOptions) {
    this.recentlyPurchasedService
      .recentlyPurchasedProductNumbers$()
      .pipe(take(1))
      .subscribe(productNumbers => {
        const uniqueProducts = productNumbers.filter(
          (val, i) => productNumbers.indexOf(val) === i,
        );
        if (downloadListOptions.includeProductPrices) {
          this.productPricingService.getPrices(uniqueProducts);
        }

        if (downloadListOptions.includeNutritionals) {
          this.productStateService.loadProductDetails(uniqueProducts);
        }
        this.productStateService.loadProducts(uniqueProducts);
      });
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private downloadListService: DownloadListService,
    private recentlyPurchasedService: RecentlyPurchasedService,
    private productPricingService: ProductPricingService,
    private productStateService: ProductStateService,
    private platformService: PlatformService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}
}
